/**
 * The notification drawer (docs/03 §7 Tier 2; docs/04 §3e; punch item 3.3).
 *
 * WHAT THIS IS NOT. It is not a third view of the expirations lanes. Every row
 * in here is addressed to the reader BY NAME — somebody asked them a question,
 * named them in a message, handed them a piece of work, or work they actually
 * own has gone past its date. "What is due across the buildings I manage" is a
 * different question, and /work plus the My Work chip already answer it. That
 * distinction is the whole design; without it the bell and the chip would be two
 * controls for one list, sat next to each other in the same bar.
 *
 * WHY IT LIVES HERE and not in `components/ui`. It knows about the router and
 * about the badge query. The PANEL it opens is the shared `Drawer`, extracted
 * when the second caller arrived (the Dev-only QA panel, L-36) — before that, a
 * shared drawer with one caller would have been a guess about the second.
 */

import {
  NOTIFICATION_LIMIT,
  type NotificationKind,
  type NotificationRow,
  type NotificationsResponse,
} from '@propos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AtSign, Bell, CalendarClock, MessageCircleQuestion, UserPlus } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';

import { api } from '../../app/api.js';
import { BADGES_KEY } from '../../app/useBadges.js';
import { Drawer, EmptyState, ErrorState, Timestamp } from '../../components/ui/index.js';

const NOTIFICATIONS_KEY = ['notifications'] as const;

/**
 * How each kind announces itself.
 *
 * An ICON AND A WORD, never a colour alone (docs/03 §8). Two of the four carry a
 * tint because two of them are actually urgent — an unanswered question and a
 * date already missed — and tinting all four would be bunting: if everything is
 * marked important, the marking says nothing. The same argument 2.1 settled for
 * the metric bar's tiles.
 *
 * `Chip` is deliberately not used. It is typed against docs/04 §4's status
 * catalog, and "somebody mentioned you" is not a status of anything — it is an
 * event. Two of these words (`Overdue`, `Unanswered`) DO exist as statuses, and
 * borrowing the chip for those two while inventing something else for the other
 * two would make one list speak two vocabularies.
 */
const KIND: Record<
  NotificationKind,
  { readonly label: string; readonly icon: ReactNode; readonly tone: string }
> = {
  ask: {
    label: 'Question for you',
    icon: <MessageCircleQuestion size={16} aria-hidden />,
    tone: 'text-amber-fg',
  },
  overdue: {
    label: 'Overdue',
    icon: <CalendarClock size={16} aria-hidden />,
    tone: 'text-red-fg',
  },
  mention: { label: 'Mentioned you', icon: <AtSign size={16} aria-hidden />, tone: 'text-ink2' },
  transfer: {
    label: 'Assigned to you',
    icon: <UserPlus size={16} aria-hidden />,
    tone: 'text-ink2',
  },
};

/**
 * The supporting line, assembled from what this row actually has.
 *
 * Built by filtering rather than by nested conditionals so an absent actor (an
 * overdue item, which nobody caused) or an absent property (a vendor's COI,
 * which belongs to no single building) simply contributes nothing — no dangling
 * separator, and no "undefined · Cameron Valley".
 */
function subline(row: NotificationRow): string {
  return [row.actorName, row.propertyName].filter((part) => part !== null).join(' · ');
}

/**
 * One notification.
 *
 * NOT the design system's `Row`, and gate 15 was right to make me say why
 * rather than quietly shadow the name. Three differences, each of which would
 * mean widening a component used on a dozen screens for one caller here:
 *
 *   * it is an `<li>` inside a list, and `Row` renders a `div` or an `a`;
 *   * its link has to be a router link — `Row`'s `href` is a raw anchor, which
 *     would reload the whole application instead of navigating;
 *   * unread is a left accent and a background, which `Row` has no notion of.
 *
 * If a second list ever wants this shape, that is the moment it moves into the
 * design system — with `Row` widened or a sibling added deliberately, not by a
 * copy that starts out looking correct.
 */
function NotificationItem({
  row,
  onNavigate,
}: {
  readonly row: NotificationRow;
  readonly onNavigate: () => void;
}): ReactNode {
  const kind = KIND[row.kind];
  const detail = subline(row);
  const body = (
    <>
      <span className={`flex items-center gap-1.5 text-chip font-semibold ${kind.tone}`}>
        {kind.icon}
        {/* Its own element, so the word is addressable on its own. It shares
            this line with an icon and (when unread) a screen-reader-only
            marker, and a bare text node among siblings is not a thing a test —
            or a person using a screen reader's element list — can point at. */}
        <span>{kind.label}</span>
        {/* The unread marker as TEXT for assistive technology. The accent bar
            below is the visual half of the same statement, and neither is the
            only carrier of it (docs/03 §8). */}
        {row.unread ? <span className="sr-only">— new</span> : null}
      </span>
      <span className="text-body text-ink">{row.title}</span>
      <span className="flex flex-wrap items-baseline gap-x-2 text-chip text-ink2">
        {detail === '' ? null : <span>{detail}</span>}
        {/* NOT `text-ink3`. That token is declared decorative-only (docs/04
            v1.3, and `scripts/ci/contrast.ts` lists it under DECORATIVE), and
            it is forbidden from carrying meaningful text — which a timestamp
            is. axe measured the pair at 4.44:1 against the card at 12px, just
            under the 4.5 floor. The contrast gate did not catch it because it
            asserts token PAIRS from docs/04, and nothing checks which token a
            given piece of text actually uses. */}
        <Timestamp value={row.occurredAt} />
      </span>
    </>
  );

  const shell =
    'flex flex-col gap-1 border-l-2 py-3 pl-3 pr-1 ' +
    (row.unread ? 'border-brand-700 bg-surface-hover/40' : 'border-transparent');

  // A row with nowhere to go is TEXT, not a dead link. A lease reads inside its
  // tenancy and a contact inside its vendor, so some records genuinely have no
  // page — and a link that lands on the wrong one is worse than a line you have
  // to go and find. Same rule the action-item screens apply.
  return row.href === null ? (
    <li className={shell}>{body}</li>
  ) : (
    <li>
      <RouterLink
        to={row.href}
        onClick={onNavigate}
        className={`${shell} no-underline hover:bg-surface-hover`}
      >
        {body}
      </RouterLink>
    </li>
  );
}

/**
 * The bell AND its drawer, as one component.
 *
 * They were two — a button in the top bar and a controlled dialog beside it —
 * and Escape then left focus on the document body instead of returning it to
 * the control that opened the panel. Somebody navigating by keyboard would have
 * had to tab back from the start of the page every time they closed it.
 *
 * Radix restores focus to its own `Trigger`, and a trigger has to live inside
 * the `Root`. Making the bell that trigger is the fix, and it also puts the
 * control and the thing it controls in one file, which is where they belonged:
 * the top bar now asks for "the notification bell" rather than assembling one.
 */
export function NotificationBell({ unread }: { readonly unread: number }): ReactNode {
  const [open, setOpen] = useState(false);
  const client = useQueryClient();

  // `enabled: open` — the drawer's contents are three queries and an expirations
  // union, and nobody is reading them while it is shut. The BELL's number comes
  // from the badges request the shell already makes, so the closed state costs
  // nothing extra.
  const query = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: () =>
      api<NotificationsResponse>(`/api/v1/me/notifications?limit=${String(NOTIFICATION_LIMIT)}`),
    enabled: open,
  });

  const markSeen = useMutation({
    mutationFn: () => api<{ seenAt: string }>('/api/v1/me/notifications/seen', { method: 'PUT' }),
    onSuccess: () => {
      /**
       * The DOT clears immediately; the ROWS keep their highlight.
       *
       * Invalidating the notifications list here would refetch it under the
       * reader's eyes and wipe every "new" marker while they are still reading
       * the list those markers are for — the bell would be telling them
       * something and taking it away in the same beat. So only the badge query
       * is invalidated now, and the list is refreshed on close (below), by which
       * point the reader has seen what was new.
       */
      void client.invalidateQueries({ queryKey: BADGES_KEY });
    },
  });

  /**
   * Opening the drawer IS reading it (0070). There is no per-item read state and
   * no "mark all read" button to press — the button would be a second way to say
   * what opening already said, and one people would forget, leaving a dot lit
   * over a list they had read.
   *
   * `open` is the ONLY dependency, deliberately. react-query hands back a new
   * mutation object on every render, so depending on `markSeen` would re-stamp
   * on every unrelated render of the shell — and `mutate` is stable across them,
   * which is why reading it from a stale closure is safe here.
   */
  const stamp = markSeen.mutate;
  useEffect(() => {
    if (!open) return;
    stamp();
  }, [open, stamp]);

  const rows = query.data?.rows ?? [];

  /*
   * The bell is a DOT, not a count, per docs/03 §7 — and the count still
   * reaches assistive technology through the accessible name, because a dot
   * says nothing to a screen reader. No dot at zero, for the reason 1.6 gave
   * about the "0" pill: a marker that is always present is a marker nobody
   * looks at. (It shipped in 1.6 wearing `aria-disabled` and an honest title;
   * docs/03 §7 calls a disabled control with no stated reason an
   * anti-pattern, and this is the drawer it was waiting for.)
   */
  const bell = (
    <button
      type="button"
      aria-label={unread === 0 ? 'Notifications' : `Notifications — ${String(unread)} new`}
      className="relative flex min-h-11 min-w-11 items-center justify-center rounded-ctl text-ink2 hover:bg-surface-hover hover:text-ink"
    >
      <Bell size={19} aria-hidden />
      {unread > 0 ? (
        <span
          aria-hidden
          className="absolute right-2.5 top-2.5 size-2 rounded-full bg-red-fg ring-2 ring-card"
        />
      ) : null}
    </button>
  );

  return (
    <Drawer
      trigger={bell}
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Refreshed on the way OUT, so the next open starts from the truth
        // rather than from a list whose "new" marks are a session old.
        if (!next) void client.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      }}
      title="Notifications"
      closeLabel="Close notifications"
      footer={
        <RouterLink
          to="/work"
          onClick={() => {
            setOpen(false);
          }}
          className="text-body text-brand-700"
        >
          Everything due across my properties
        </RouterLink>
      }
    >
      {query.isError ? (
        <ErrorState
          body="Your notifications could not be loaded."
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : query.isPending ? (
        // A plain sentence rather than a skeleton: the panel is one column
        // of short rows, and a skeleton of it is a picture of a list that
        // takes longer to read than the list.
        <p className="py-8 text-center text-body text-ink2">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing for you right now"
          body="Questions, @mentions, work handed to you and anything of yours that has gone past its date will show up here."
        />
      ) : (
        <>
          <ul className="flex list-none flex-col divide-y divide-hairline p-0">
            {rows.map((row) => (
              <NotificationItem
                key={row.key}
                row={row}
                onNavigate={() => {
                  setOpen(false);
                }}
              />
            ))}
          </ul>
          {/* Said plainly rather than implied by a list that just stops.
              Both kinds that can reach the cap — unanswered questions and
              overdue work — are reachable in full on their own screens. */}
          {query.data.truncated ? (
            <p className="py-3 text-center text-chip text-ink2">
              Showing the {NOTIFICATION_LIMIT} most recent. Older items stay on their records.
            </p>
          ) : null}
        </>
      )}
    </Drawer>
  );
}
